import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import debounce from "lodash.debounce";
import {
  HomePageContainer,
  HeaderWrapper,
  Button,
  Image,
  HomeContainer,
  Row,
  Column,
  Text,
  TextLight,
  ButtonBig,
  Wrapper,
  Input,
  Username,
  WishlistName,
  ProfilePhoto,
  UserContainer,
  Users,
  SearchUser,
  SearchListContainer,
  SearchContainer,
  BackContainer,
  BackRow,
  BackText,
  LoaderContainer,
  StyledText,
  NoProfilePhoto,
  IconGift,
  ButtonDisabled,
  StyledRow,
  ModalTitle,
  ModalText,
  ModalRow,
  ModalButton,
  CreateRow,
  CloseIcon,
  StyledButton,
  StyledButtonDisabledBig,
  CloseIconRow,
  StyledRowOrg,
  SearchRow,
  SearchInput,
  ModalCloseIcon,
  StyledInfinityScroll,
  StyledInfinityScrollSearch,
  DescriptionInput,
  UserButtonContainer,
} from "./DashboardPage.styles.js";

import Header from "../../components/Header/Header.js";
import Footer from "../../components/Footer/Footer.js";
import Loader from "../../components/Loader/Loader";
import Icon from "../../components/utils/Icon.js";
import Modal from "../../components/Modal/Modal.js";
import openToast from "../../utils/toast.js";

import OrgLogo from "../../assets/images/orgLogo.png";
import { ReactComponent as Add } from "../../assets/icons/add.svg";
import { ReactComponent as Back } from "../../assets/icons/chevron.svg";
import { ReactComponent as DragIcon } from "../../assets/icons/drag.svg";
import { ReactComponent as Loading } from "../../assets/icons/loader-white.svg";
import { ReactComponent as LoadingGreen } from "../../assets/icons/loader-green.svg";

import { selectedLocale } from "../../store/slices/common/slice.js";
import {
  addNewOrganization,
  editOrganizationMemberDescription,
  removeMember,
  removeOrganization,
  setActiveUserOrganization,
  setPriorityToOrganizationMembers,
  setPriorityToOrganizations,
} from "../../store/slices/organization/asyncThunks.js";
import {
  addMemberToOrganization,
  getOrganizationMembers,
  searchUsers,
  setOrganizationDescription,
  setOrganizationLogo,
} from "../../store/slices/organization/asyncThunks.js";
import {
  resetSearchUsers,
  selectAddMemberToOrganizationPending,
  selectAddNewOrganizationPending,
  selectAllUserOrganizations,
  selectEditOrganizationMemberDescriptionPending,
  selectFetchUserPending,
  selectGetOrganizationMembersPending,
  selectOrganizationMembers,
  selectRemoveMemberPending,
  selectRemoveOrganizationPending,
  selectSearchUsers,
  selectSearchUsersPending,
  selectSetActiveUserOrganizationPending,
  selectSetOrganizationDescriptionPending,
  selectSetOrganizationLogoPending,
  selectUserInfo,
} from "../../store/slices/organization/slice.js";
import organizationService from "../../api/services/organization.js";

function UserCard({
  user,
  setDeletingMemberId,
  setOpenRemoveModal,
  deletingMemberId,
  removeMemberLoading,
  navigate,
  drag,
  t,
  setSaveMemberId,
  saveMemberId,
  toastId,
  saveActionPending,
  dispatch,
}) {
  const [description, setDescription] = useState(user.description ?? "");

  return (
    <StyledRow $drag={drag}>
      {drag && <DragIcon />}
      <UserContainer
        onClick={() => {
          window.scrollTo(0, 0);
          navigate(`/wishlist/${user.userId}`, {
            state: { wishlistName: user?.fullName, username: user?.username },
          });
        }}
      >
        {user?.profilePhoto ? (
          <ProfilePhoto src={user?.profilePhoto} />
        ) : (
          <NoProfilePhoto>
            <IconGift />
          </NoProfilePhoto>
        )}
        <Column>
          <WishlistName>
            {user?.fullName?.length > 25
              ? user?.fullName.slice(0, 25) + "..."
              : user?.fullName}
          </WishlistName>
          <Username>
            @
            {user?.username?.length > 25
              ? user?.username.slice(0, 25) + "..."
              : user?.username}
          </Username>
        </Column>
      </UserContainer>

      <DescriptionInput
        type="text"
        placeholder="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      ></DescriptionInput>

      <UserButtonContainer>
        {user?.id === saveMemberId && saveActionPending ? (
          <ButtonDisabled>
            <Loading width={16} height={16} />
          </ButtonDisabled>
        ) : (
          <Button
            onClick={async () => {
              setSaveMemberId(user.id);
              dispatch(
                editOrganizationMemberDescription({ description, id: user.id })
              )
                .unwrap()
                .then(() => {
                  setSaveMemberId(user.id);
                })
                .catch((err) => {
                  openToast(toastId, "active-org", "error", err.message);
                });
            }}
          >
            {t("save")}
          </Button>
        )}

        {user?.id === deletingMemberId && removeMemberLoading ? (
          <ButtonDisabled>
            <Loading width={16} height={16} />
          </ButtonDisabled>
        ) : (
          <Button
            onClick={() => {
              setOpenRemoveModal({
                state: true,
                type: "member",
              });
              setDeletingMemberId(user.id);
            }}
          >
            {t("remove")}
          </Button>
        )}
      </UserButtonContainer>
    </StyledRow>
  );
}

function OrganizationCard({
  org,
  drag,
  userInfo,
  dispatch,
  setTab,
  reset,
  toastId,
  setDeletingOrgId,
  removeOrgLoading,
  deletingOrgId,
  setOpenRemoveModal,
  t,
}) {
  return (
    <StyledRow $drag={drag}>
      {drag && <DragIcon />}
      <UserContainer
        onClick={() => {
          window.scrollTo(0, 0);
          if (userInfo?.activeOrganizationId === org.id) {
            setTab(0);
            reset();
          } else {
            dispatch(setActiveUserOrganization(org.id))
              .unwrap()
              .then(() => {
                setTab(0);
                reset();
              })
              .catch((err) =>
                openToast(toastId, "active-org", "error", err.message)
              );
          }
        }}
      >
        {org?.photoUrl ? (
          <ProfilePhoto
            src={org?.photoUrl}
            style={{ minHeight: 50, minWidth: 50 }}
          />
        ) : (
          <ProfilePhoto
            src={OrgLogo}
            style={{
              minHeight: 50,
              minWidth: 50,
            }}
          />
        )}
        <Column>
          <WishlistName>
            {org?.name?.length > 25
              ? org?.name.slice(0, 25) + "..."
              : org?.name}
          </WishlistName>
          {org?.description && (
            <Username>
              {org?.description?.length > 70
                ? org?.description.slice(0, 70) + "..."
                : org?.description}
            </Username>
          )}
        </Column>
      </UserContainer>

      {userInfo?.activeOrganizationId !== org.id &&
        (org?.id === deletingOrgId && removeOrgLoading ? (
          <ButtonDisabled>
            <Loading width={16} height={16} />
          </ButtonDisabled>
        ) : (
          <Button
            onClick={() => {
              setOpenRemoveModal({
                state: true,
                type: "organization",
              });
              setDeletingOrgId(org.id);
            }}
          >
            {t("remove")}
          </Button>
        ))}
    </StyledRow>
  );
}

function SearchUserCard({
  user,
  addingUserId,
  handleAddUser,
  addMemberToOrganizationPending,
  t,
}) {
  return (
    <SearchUser>
      {user?.profilePhoto ? (
        <ProfilePhoto src={user?.profilePhoto} />
      ) : (
        <NoProfilePhoto>
          <IconGift />
        </NoProfilePhoto>
      )}
      <Column>
        <WishlistName>
          {user?.fullName?.length > 25
            ? user?.fullName.slice(0, 25) + "..."
            : user?.fullName}
        </WishlistName>
        <Username>
          @
          {user?.username?.length > 25
            ? user?.username.slice(0, 25) + "..."
            : user?.username}
        </Username>
      </Column>

      {user?.id === addingUserId && addMemberToOrganizationPending ? (
        <Button $disabled>
          <Loading width={16} height={16} />
        </Button>
      ) : (
        <Button
          onClick={() => {
            handleAddUser(user?.id);
          }}
        >
          <Icon icon={<Add />} marginBottom={"-3px"} />
          {t("add")}
        </Button>
      )}
    </SearchUser>
  );
}

const DashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toastId = useRef(null);

  const locale = useSelector(selectedLocale);
  const ar = locale === "ar";

  const userInfo = useSelector(selectUserInfo);
  const selectFetchUserLoading = useSelector(selectFetchUserPending);
  const userOrganizations = useSelector(selectAllUserOrganizations);
  const organizationMembers = useSelector(selectOrganizationMembers);
  const searchUsersData = useSelector(selectSearchUsers);

  const setActiveUserOrganizationLoading = useSelector(
    selectSetActiveUserOrganizationPending
  );
  const setOrganizationDescriptionPending = useSelector(
    selectSetOrganizationDescriptionPending
  );
  const setOrganizationLogoPending = useSelector(
    selectSetOrganizationLogoPending
  );
  const getOrganizationMembersLoading = useSelector(
    selectGetOrganizationMembersPending
  );

  const [description, setDescription] = useState("");
  const [logo, setLogo] = useState(null);

  const [edit, setEdit] = useState(false);
  const [addUser, setAddUser] = useState(false);
  const [searchUsername, setSearchUsername] = useState("");
  const [organizationName, setOrganizationName] = useState("");

  const [tab, setTab] = useState(0);
  const [isVisible, setIsVisible] = useState(!!searchUsername.length);
  const [isVisibleTab, setIsVisibleTab] = useState(tab === 0);
  const [showListLoading, setShowListLoading] = useState(true);
  const [addingUserId, setAddingUserId] = useState(null);
  const [deletingOrgId, setDeletingOrgId] = useState(null);
  const [deletingMemberId, setDeletingMemberId] = useState(null);
  const [openRemoveModal, setOpenRemoveModal] = useState(false);

  const [firstLoad, setFirstLoad] = useState(true);

  const [members, setMembers] = useState([]);
  const [organizations, setOrganizations] = useState([]);

  const [createOrganization, setCreateOrganization] = useState(false);
  const [isVisibleButtonText, setIsVisibleButtonText] =
    useState(createOrganization);

  const removeMemberLoading = useSelector(selectRemoveMemberPending);
  const removeOrgLoading = useSelector(selectRemoveOrganizationPending);

  const addNewOrganizationLoading = useSelector(
    selectAddNewOrganizationPending
  );
  const addMemberToOrganizationPending = useSelector(
    selectAddMemberToOrganizationPending
  );
  const searchUsersPending = useSelector(selectSearchUsersPending);

  const saveActionPending = useSelector(
    selectEditOrganizationMemberDescriptionPending
  );

  const [saveMemberId, setSaveMemberId] = useState(null);

  useEffect(() => {
    if (userOrganizations?.length) {
      setOrganizations(userOrganizations);
    }
  }, []);

  useEffect(() => {
    if (userInfo.organizationPhotoUrl) {
      setLogo(userInfo.organizationPhotoUrl);
    } else {
      setLogo(OrgLogo);
    }
  }, [userInfo]);

  useEffect(() => {
    if (userInfo?.description) {
      setDescription(userInfo?.description);
    } else {
      setDescription("");
    }
  }, [userInfo]);

  useEffect(() => {
    if (userInfo.activeOrganizationId) {
      dispatch(getOrganizationMembers({ page: 1 }))
        .unwrap()
        .then((response) => {
          setFirstLoad(false);
          setMembers(response.data);
        })
        .catch((err) =>
          openToast(toastId, "error-fetch-members", "error", err.message)
        );
    }
  }, [dispatch, userInfo]);

  useEffect(() => {
    let timer;
    if (!!searchUsername.length) {
      setIsVisible(true);
    } else {
      timer = setTimeout(() => setIsVisible(false), 200);
    }

    return () => clearTimeout(timer);
  }, [searchUsername]);

  useEffect(() => {
    let timer;
    if (tab === 0) {
      setIsVisibleTab(true);
    } else {
      timer = setTimeout(() => setIsVisibleTab(false), 200);
    }

    return () => clearTimeout(timer);
  }, [tab]);

  useEffect(() => {
    let timer;
    if (createOrganization) {
      setIsVisibleButtonText(true);
    } else {
      timer = setTimeout(() => setIsVisibleButtonText(false), 80);
    }

    return () => clearTimeout(timer);
  }, [createOrganization]);

  useEffect(() => {
    if (!userInfo?.activeOrganizationId) {
      setTab(1);
    }
  }, [userInfo]);

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const logoUrl = URL.createObjectURL(file);
      setLogo(logoUrl);
    }
    dispatch(
      setOrganizationLogo({
        file,
        organizationId: userInfo?.activeOrganizationId,
      })
    )
      .unwrap()
      .then(() => {})
      .catch((error) => {
        openToast(toastId, "upload-logo", "error", error.message);
      });
  };

  const handleAddUser = (id) => {
    setAddingUserId(id);

    dispatch(
      addMemberToOrganization({
        organizationId: userInfo?.activeOrganizationId,
        userId: id,
      })
    )
      .unwrap()
      .then((data) => {
        setAddingUserId(null);
        setMembers((prev) => {
          return [data.data, ...prev];
        });
      })
      .catch((error) => {
        openToast(toastId, "add-user", "error", error.message);
      });
  };

  const handleDescriptionSave = () => {
    if (description) {
      dispatch(
        setOrganizationDescription({
          description,
          organizationId: userInfo?.activeOrganizationId,
        })
      )
        .unwrap()
        .then(() => {
          setEdit(false);
        })
        .catch((error) => {
          openToast(toastId, "add-description", "error", error.message);
        });
    } else {
      setEdit(false);
    }
  };

  const reset = () => {
    setAddUser(false);
    setSearchUsername("");
    setEdit(false);
  };

  const changeHandler = useCallback(
    (text) => {
      if (text !== "") {
        setShowListLoading(true);
        dispatch(searchUsers({ page: 1, search: text }))
          .unwrap()
          .then(() => {
            if (showListLoading) {
              setShowListLoading(false);
            }
          })
          .catch((error) => {
            openToast(toastId, "search-user", "error", error.message);
          });
      } else {
        setShowListLoading(true);
        dispatch(resetSearchUsers());
      }
    },
    [dispatch]
  );

  const debouncedChangeHandler = useMemo(
    () => debounce(changeHandler, 300),
    [changeHandler]
  );

  const handleChange = (value) => {
    debouncedChangeHandler(value);
  };

  const loadMoreItems = () => {
    if (!organizationMembers?.maxReached && !getOrganizationMembersLoading) {
      dispatch(getOrganizationMembers({ page: organizationMembers.page + 1 }))
        .unwrap()
        .then((response) => {
          setFirstLoad(false);
          setMembers((prev) => {
            return [...prev, ...response.data];
          });
        })
        .catch((err) =>
          openToast(toastId, "error-load-more-members", "error", err.message)
        );
    }
  };

  const loadMoreItemsSearch = () => {
    if (!searchUsersData?.maxReached && !searchUsersPending) {
      dispatch(
        searchUsers({ page: searchUsersData.page + 1, search: searchUsername })
      )
        .unwrap()
        .then(() => {})
        .catch((err) =>
          openToast(
            toastId,
            "error-load-more-search-members",
            "error",
            err.message
          )
        );
    }
  };

  const handleMembersOnDragEnd = (result) => {
    if (!result.destination) return;

    const reorderedMembers = Array.from(members);
    const [removed] = reorderedMembers.splice(result.source.index, 1);
    reorderedMembers.splice(result.destination.index, 0, removed);

    const formattedMembers = reorderedMembers.map((member, index) => {
      return { id: member.id, index };
    });

    dispatch(setPriorityToOrganizationMembers(formattedMembers))
      .unwrap()
      .then(() => {})
      .catch((err) =>
        openToast(toastId, "error-priority-members", "error", err.message)
      );
    setMembers(reorderedMembers);
  };

  const handleOrganizationsOnDragEnd = (result) => {
    if (!result.destination) return;

    const reorderedOrganizations = Array.from(organizations);
    const [removed] = reorderedOrganizations.splice(result.source.index, 1);
    reorderedOrganizations.splice(result.destination.index, 0, removed);

    const formattedOrganizations = reorderedOrganizations.map((org, index) => {
      return { id: org.id, index };
    });

    dispatch(setPriorityToOrganizations(formattedOrganizations))
      .unwrap()
      .then(() => {})
      .catch((err) =>
        openToast(toastId, "error-priority-organizations", "error", err.message)
      );
    setOrganizations(reorderedOrganizations);
  };

  return (
    <>
      <HeaderWrapper>
        <Header />
      </HeaderWrapper>

      <HomeContainer>
        {!setActiveUserOrganizationLoading && !selectFetchUserLoading ? (
          isVisibleTab ? (
            <HomePageContainer $open={tab === 0}>
              <BackContainer>
                <BackRow>
                  <BackText
                    onClick={() => {
                      window.scrollTo(0, 0);
                      setTab(1);
                      reset();
                    }}
                  >
                    {t("change-organization")}
                  </BackText>
                </BackRow>
              </BackContainer>

              <StyledRowOrg>
                <Image src={logo} alt="Logo-Upload" />

                <input
                  type="file"
                  id="logoUpload"
                  style={{ display: "none" }}
                  onChange={handleLogoUpload}
                  accept="image/*"
                />
                {setOrganizationLogoPending ? (
                  <ButtonDisabled style={{ width: 95 }}>
                    <Loading width={16} height={16} />
                  </ButtonDisabled>
                ) : (
                  <Button
                    onClick={() => {
                      document.getElementById("logoUpload").click();
                    }}
                  >
                    {t("attach-logo")}
                  </Button>
                )}
              </StyledRowOrg>
              <Row>
                <Column>
                  <Text $bold>{userInfo?.organizationName}</Text>
                  <Text>{t("organization-description")}</Text>
                  <TextLight
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    $edit={edit}
                    disabled={!edit}
                  />
                </Column>
                {edit ? (
                  setOrganizationDescriptionPending ? (
                    <ButtonDisabled style={{ width: 95 }}>
                      <Loading width={16} height={16} />
                    </ButtonDisabled>
                  ) : (
                    <Button
                      style={{ width: 95 }}
                      onClick={() => {
                        handleDescriptionSave();
                      }}
                    >
                      {t("save")}
                    </Button>
                  )
                ) : (
                  <Button style={{ width: 95 }} onClick={() => setEdit(true)}>
                    {t("edit")}
                  </Button>
                )}
              </Row>
              <Wrapper>
                <SearchRow $flexStart $open={!!searchUsername.length}>
                  <ButtonBig
                    onClick={() => {
                      setAddUser(true);
                    }}
                  >
                    {t("add-a-new-user")}
                  </ButtonBig>

                  <SearchContainer style={{ opacity: addUser ? 1 : 0 }}>
                    <CloseIconRow>
                      <SearchInput
                        disabled={!addUser}
                        $borderRadius={!!searchUsername.length}
                        placeholder={t("username")}
                        value={searchUsername}
                        onChange={(e) => {
                          handleChange(e.target.value);
                          setSearchUsername(e.target.value);
                        }}
                      />
                      <CloseIcon
                        onClick={() => {
                          setSearchUsername("");
                          setAddUser(false);
                        }}
                      />
                    </CloseIconRow>

                    {isVisible && (
                      <SearchListContainer
                        $open={!!searchUsername.length}
                        style={{
                          justifyContent:
                            searchUsersData?.data.length === 0
                              ? "center"
                              : "flex-start",
                          alignItems:
                            searchUsersData?.data.length === 0
                              ? "center"
                              : "flex-start",
                        }}
                      >
                        {!showListLoading ? (
                          searchUsersData?.data?.length > 0 ? (
                            <div
                              id="scrollableDiv"
                              style={{ overflowY: "scroll" }}
                            >
                              <StyledInfinityScrollSearch
                                dataLength={searchUsersData?.data?.length ?? 0}
                                hasMore={!searchUsersData?.maxReached}
                                next={loadMoreItemsSearch}
                                loader={<LoadingGreen width={32} height={32} />}
                                scrollableTarget="scrollableDiv"
                              >
                                {searchUsersData?.data?.map((user) => (
                                  <SearchUserCard
                                    key={user.id}
                                    user={user}
                                    addingUserId={addingUserId}
                                    handleAddUser={handleAddUser}
                                    addMemberToOrganizationPending={
                                      addMemberToOrganizationPending
                                    }
                                    t={t}
                                  />
                                ))}
                              </StyledInfinityScrollSearch>
                            </div>
                          ) : (
                            <StyledText>{t("no-users")}</StyledText>
                          )
                        ) : (
                          <LoadingGreen width={32} height={32} />
                        )}
                      </SearchListContainer>
                    )}
                  </SearchContainer>
                </SearchRow>
              </Wrapper>
              <Users>
                {!firstLoad ? (
                  members?.length > 0 ? (
                    <StyledInfinityScroll
                      dataLength={members.length ?? 0}
                      hasMore={!organizationMembers?.maxReached}
                      next={loadMoreItems}
                      loader={<LoadingGreen width={32} height={32} />}
                    >
                      <DragDropContext onDragEnd={handleMembersOnDragEnd}>
                        <Droppable droppableId="members">
                          {(provided) => (
                            <div
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "10px",
                              }}
                            >
                              {members.map((user, index) => (
                                <Draggable
                                  key={user.id}
                                  draggableId={user.id.toString()}
                                  index={index}
                                >
                                  {(provided) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                    >
                                      <UserCard
                                        user={user}
                                        drag
                                        setDeletingMemberId={
                                          setDeletingMemberId
                                        }
                                        setOpenRemoveModal={setOpenRemoveModal}
                                        deletingMemberId={deletingMemberId}
                                        removeMemberLoading={
                                          removeMemberLoading
                                        }
                                        navigate={navigate}
                                        t={t}
                                        dispatch={dispatch}
                                        toastId={toastId}
                                        saveActionPending={saveActionPending}
                                        setSaveMemberId={setSaveMemberId}
                                        saveMemberId={saveMemberId}
                                      />
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                    </StyledInfinityScroll>
                  ) : (
                    <StyledText>{t("no-organization-members")}</StyledText>
                  )
                ) : (
                  <LoadingGreen width={50} height={50} />
                )}
              </Users>
            </HomePageContainer>
          ) : (
            <HomePageContainer $open={tab === 1}>
              <BackContainer>
                <BackRow
                  onClick={() => {
                    reset();
                    setTab(0);
                  }}
                >
                  <Back />
                  <BackText>{t("back")}</BackText>
                </BackRow>
              </BackContainer>

              <Users>
                <Text style={{ marginBottom: 10 }}>{t("organizations")}</Text>

                <DragDropContext onDragEnd={handleOrganizationsOnDragEnd}>
                  <Droppable droppableId="organizations">
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "10px",
                        }}
                      >
                        {organizations.map((org, index) => (
                          <Draggable
                            key={org.id}
                            draggableId={org.id.toString()}
                            index={index}
                          >
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <OrganizationCard
                                  drag
                                  org={org}
                                  userInfo={userInfo}
                                  dispatch={dispatch}
                                  setTab={setTab}
                                  reset={reset}
                                  toastId={toastId}
                                  setDeletingOrgId={setDeletingOrgId}
                                  removeOrgLoading={removeOrgLoading}
                                  deletingOrgId={deletingOrgId}
                                  setOpenRemoveModal={setOpenRemoveModal}
                                  t={t}
                                />
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>

                <CreateRow>
                  {addNewOrganizationLoading ? (
                    <StyledButtonDisabledBig>
                      <Loading width={16} height={16} />
                    </StyledButtonDisabledBig>
                  ) : createOrganization ? (
                    <StyledButton
                      style={{ width: createOrganization ? 89 : 173.2 }}
                      onClick={() => {
                        dispatch(addNewOrganization(organizationName))
                          .unwrap()
                          .then((response) => {
                            setOrganizations((prev) => {
                              return [response.data, ...prev];
                            });
                            setOrganizationName("");
                            setCreateOrganization(false);
                          })
                          .catch((err) =>
                            openToast(toastId, "add-org", "error", err.message)
                          );
                      }}
                    >
                      <Add />
                      {t("add")}
                    </StyledButton>
                  ) : (
                    <StyledButton
                      style={{ width: createOrganization ? 89 : 173.2 }}
                      onClick={() => {
                        setCreateOrganization(true);
                      }}
                    >
                      {!isVisibleButtonText && t("create-organization")}
                    </StyledButton>
                  )}

                  <CloseIconRow style={{ opacity: createOrganization ? 1 : 0 }}>
                    <Input
                      disabled={!createOrganization}
                      style={{ width: "30rem" }}
                      placeholder={t("organization-name")}
                      value={organizationName}
                      onChange={(e) => setOrganizationName(e.target.value)}
                    />
                    <CloseIcon
                      onClick={() => {
                        setOrganizationName("");
                        setCreateOrganization(false);
                      }}
                    />
                  </CloseIconRow>
                </CreateRow>
              </Users>
            </HomePageContainer>
          )
        ) : (
          <LoaderContainer>
            <Loader />
          </LoaderContainer>
        )}

        <Footer ar={ar} />

        <Modal
          open={openRemoveModal.state}
          close={setOpenRemoveModal}
          type={openRemoveModal.type}
        >
          {openRemoveModal.type === "organization" ? (
            <>
              <ModalTitle>{t("remove-organization-title")}</ModalTitle>
              <ModalText>{t("remove-organization-text")}</ModalText>

              <ModalCloseIcon
                onClick={() => {
                  setDeletingOrgId(null);
                  setOpenRemoveModal({
                    state: false,
                    type: "organization",
                  });
                }}
              />
            </>
          ) : (
            <>
              <ModalTitle>{t("remove-member-title")}</ModalTitle>
              <ModalText>{t("remove-member-text")}</ModalText>

              <ModalCloseIcon
                onClick={() => {
                  setDeletingMemberId(null);
                  setOpenRemoveModal({
                    state: false,
                    type: "member",
                  });
                }}
              />
            </>
          )}

          <ModalRow>
            <ModalButton onClick={() => setOpenRemoveModal(false)}>
              {t("no")}
            </ModalButton>
            <ModalButton
              $yes
              onClick={() => {
                if (openRemoveModal.type === "organization") {
                  setOpenRemoveModal({
                    state: false,
                    type: "organization",
                  });

                  dispatch(removeOrganization(deletingOrgId))
                    .unwrap()
                    .then((response) => {
                      setOrganizations((prev) => {
                        return prev.filter(
                          (org) => org.id !== response?.data?.id
                        );
                      });
                      setDeletingOrgId(null);
                    })
                    .catch((error) => {
                      openToast(toastId, "remove-org", "error", error.message);
                    });
                } else {
                  setOpenRemoveModal({ state: false, type: "member" });
                  dispatch(removeMember(deletingMemberId))
                    .unwrap()
                    .then((response) => {
                      setMembers((prev) => {
                        return prev.filter(
                          (user) => user.id !== response?.data?.id
                        );
                      });
                      setDeletingMemberId(null);
                    })
                    .catch((err) => {
                      openToast(toastId, "remove-member", "error", err.message);
                    });
                }
              }}
            >
              {t("yes")}
            </ModalButton>
          </ModalRow>
        </Modal>
      </HomeContainer>
    </>
  );
};

export default DashboardPage;
